import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, MapPin, Building2, CheckCircle2, Send, Loader2, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useSeo } from "@/lib/seo";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const REASONS = [
  "General inquiry", "Product support", "Partnership", "Demonstration",
  "Business opportunity", "Media inquiry", "Other",
];
const PRODUCTS = [
  "MedPolicyRef", "RouteCast", "Emergent VPN", "Endoscopy Nurse Pro",
  "Patriot News US", "TikHooks2", "Hildreth Technologies LLC", "Other",
];

const emptyForm = {
  full_name: "", email: "", company: "", reason: "", product: "",
  message: "", consent: false, website: "",
};

export default function Contact() {
  useSeo({
    title: "Contact",
    description:
      "Contact HILDRETH TECHNOLOGIES LLC for general inquiries, business partnerships, product questions, demonstrations, and media requests.",
    path: "/contact",
  });

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2)
      e.full_name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Please enter a valid email address.";
    if (!form.reason) e.reason = "Please select a reason.";
    if (!form.product) e.product = "Please select a product of interest.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please enter a message (at least 10 characters).";
    if (!form.consent) e.consent = "Please acknowledge the Privacy Policy.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        ...form,
        source_page: "Contact",
      });
      if (data?.success) {
        setSubmitted(true);
        setForm(emptyForm);
        toast.success("Message sent. Thank you for reaching out.");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "We couldn't send your message. Please try again or email us directly."
      );
      // Message is preserved because we do not clear the form on failure.
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="mx-auto flex max-w-2xl flex-col items-center px-5 py-28 text-center sm:px-8">
        <span
          data-testid="contact-success"
          className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300"
        >
          <CheckCircle2 size={34} />
        </span>
        <h1 className="mt-6 font-display text-3xl font-semibold text-white">
          Thank you — your message has been received
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-400">
          A member of HILDRETH TECHNOLOGIES LLC will review your inquiry and
          respond to the email address you provided. Your submission has been
          securely recorded.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Button
            data-testid="contact-send-another"
            onClick={() => setSubmitted(false)}
            className="rounded-full bg-sky-500 px-6 font-semibold text-[#040B16] hover:bg-sky-400"
          >
            Send another message
          </Button>
          <Button
            asChild
            variant="outline"
            className="rounded-full border-slate-600 bg-transparent px-6 font-semibold text-white hover:bg-slate-800"
          >
            <Link to="/our-work">
              Explore Our Work <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    );
  }

  return (
    <div>
      <section className="relative overflow-hidden border-b border-slate-800/70">
        <div className="absolute inset-0 ht-hero-glow" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-sky-400">
            Contact
          </p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Let's talk about your inquiry
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">
            Reach out for general inquiries, business partnerships, product
            questions, demonstrations, customer support routing, or media and
            professional inquiries.
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-3">
        {/* Info */}
        <div className="space-y-6 lg:col-span-1">
          <div className="rounded-xl border border-slate-800 bg-[#0B1527] p-6">
            <h2 className="font-display text-lg font-semibold text-white">
              HILDRETH TECHNOLOGIES LLC
            </h2>
            <ul className="mt-5 space-y-4 text-sm text-slate-300">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-0.5 shrink-0 text-sky-400" />
                <span>Okeechobee, Florida</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="mt-0.5 shrink-0 text-sky-400" />
                <a
                  href="mailto:support@hildrethtechnology.com"
                  data-testid="contact-info-email"
                  className="transition-colors hover:text-white"
                >
                  support@hildrethtechnology.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Building2 size={18} className="mt-0.5 shrink-0 text-sky-400" />
                <span>Florida limited liability company</span>
              </li>
            </ul>
          </div>
          <div className="rounded-xl border border-slate-800 bg-[#0B1527] p-6 text-sm leading-relaxed text-slate-400">
            <p>
              If the form is temporarily unavailable, you can email us directly
              at{" "}
              <a
                href="mailto:support@hildrethtechnology.com?subject=Inquiry%20—%20Hildreth%20Technologies%20LLC"
                data-testid="contact-mailto-fallback"
                className="font-medium text-sky-300 hover:text-sky-200"
              >
                support@hildrethtechnology.com
              </a>
              .
            </p>
          </div>
        </div>

        {/* Form */}
        <form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          noValidate
          className="rounded-xl border border-slate-800 bg-[#0B1527] p-6 sm:p-8 lg:col-span-2"
        >
          {/* Honeypot (hidden from users) */}
          <div className="absolute left-[-9999px]" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
            />
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="full_name" className="text-slate-200">
                Full name <span className="text-sky-400">*</span>
              </Label>
              <Input
                id="full_name"
                data-testid="contact-full-name"
                value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)}
                placeholder="Jane Doe"
                className="mt-2 border-slate-700 bg-[#040B16] text-white placeholder:text-slate-500"
              />
              {errors.full_name && (
                <p data-testid="error-full-name" className="mt-1.5 text-xs text-red-400">
                  {errors.full_name}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-200">
                Email address <span className="text-sky-400">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@company.com"
                className="mt-2 border-slate-700 bg-[#040B16] text-white placeholder:text-slate-500"
              />
              {errors.email && (
                <p data-testid="error-email" className="mt-1.5 text-xs text-red-400">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="company" className="text-slate-200">
              Company or organization <span className="text-slate-500">(optional)</span>
            </Label>
            <Input
              id="company"
              data-testid="contact-company"
              value={form.company}
              onChange={(e) => set("company", e.target.value)}
              placeholder="Company name"
              className="mt-2 border-slate-700 bg-[#040B16] text-white placeholder:text-slate-500"
            />
          </div>

          <div className="mt-5 grid gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-slate-200">
                Reason for contacting <span className="text-sky-400">*</span>
              </Label>
              <Select value={form.reason} onValueChange={(v) => set("reason", v)}>
                <SelectTrigger
                  data-testid="contact-reason"
                  className="mt-2 border-slate-700 bg-[#040B16] text-white"
                >
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-[#0B1527] text-white">
                  {REASONS.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`reason-option-${r.toLowerCase().replace(/\s+/g, "-")}`}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.reason && (
                <p data-testid="error-reason" className="mt-1.5 text-xs text-red-400">
                  {errors.reason}
                </p>
              )}
            </div>

            <div>
              <Label className="text-slate-200">
                Product of interest <span className="text-sky-400">*</span>
              </Label>
              <Select value={form.product} onValueChange={(v) => set("product", v)}>
                <SelectTrigger
                  data-testid="contact-product"
                  className="mt-2 border-slate-700 bg-[#040B16] text-white"
                >
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-[#0B1527] text-white">
                  {PRODUCTS.map((p) => (
                    <SelectItem key={p} value={p} data-testid={`product-option-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.product && (
                <p data-testid="error-product" className="mt-1.5 text-xs text-red-400">
                  {errors.product}
                </p>
              )}
            </div>
          </div>

          <div className="mt-5">
            <Label htmlFor="message" className="text-slate-200">
              Message <span className="text-sky-400">*</span>
            </Label>
            <Textarea
              id="message"
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={5}
              placeholder="How can we help?"
              className="mt-2 border-slate-700 bg-[#040B16] text-white placeholder:text-slate-500"
            />
            {errors.message && (
              <p data-testid="error-message" className="mt-1.5 text-xs text-red-400">
                {errors.message}
              </p>
            )}
          </div>

          <div className="mt-5 flex items-start gap-3">
            <Checkbox
              id="consent"
              data-testid="contact-consent"
              checked={form.consent}
              onCheckedChange={(v) => set("consent", v === true)}
              className="mt-0.5 border-slate-600 data-[state=checked]:bg-sky-500 data-[state=checked]:text-[#040B16]"
            />
            <Label htmlFor="consent" className="text-sm font-normal leading-relaxed text-slate-300">
              I acknowledge that I have read and agree to the{" "}
              <Link to="/privacy-policy" className="font-medium text-sky-300 hover:text-sky-200">
                Privacy Policy
              </Link>
              . <span className="text-sky-400">*</span>
            </Label>
          </div>
          {errors.consent && (
            <p data-testid="error-consent" className="mt-1.5 text-xs text-red-400">
              {errors.consent}
            </p>
          )}

          <Button
            type="submit"
            data-testid="contact-submit"
            disabled={submitting}
            className="mt-7 w-full rounded-full bg-sky-500 py-6 text-base font-semibold text-[#040B16] hover:bg-sky-400 disabled:opacity-60 sm:w-auto sm:px-8"
          >
            {submitting ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={18} className="mr-2" /> Send Message
              </>
            )}
          </Button>
        </form>
      </section>
    </div>
  );
}
